import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { WordReveal } from '@/components/ui/word-reveal';

interface GlassmorphicCardItem {
  title: string;
  desc: string;
  tags: string[];
}

export function ServicesSection() {
  const serviceCards: GlassmorphicCardItem[] = [
    {
      title: 'GST Registration',
      desc: 'New registration, amendment, filing and end-to-end compliance',
      tags: ['3–5 Days', 'CA Verified', 'GSTIN Issued'],
    },
    {
      title: 'Company Incorporation',
      desc: 'Private Limited, OPC, LLP incorporation with full documentation',
      tags: ['Pvt Ltd / OPC', 'DSC & DIN', 'MOA & AOA'],
    },
    {
      title: 'Trademark Registration',
      desc: 'Comprehensive brand search, application filing & objection response',
      tags: ['Brand Search', 'Class Filing', 'TM Registry'],
    },
    {
      title: 'Income Tax Services',
      desc: 'Expert return filing, PAN, TDS computation & tax planning',
      tags: ['ITR-1 to ITR-7', 'TDS & Refund', 'Audit Support'],
    },
    {
      title: 'Business Licenses',
      desc: 'Trade license, shop act, FSSAI & government registrations',
      tags: ['FSSAI License', 'Shop Act', 'MSME Udyam'],
    },
    {
      title: 'Other Services',
      desc: 'Tailored help with ROC annual filings & business compliance',
      tags: ['ROC Filing', 'Legal Consultation', 'Compliance Audit'],
    },
  ];

  return (
    <section className="bg-white py-20 md:py-28 border-t border-gray-100 relative overflow-hidden">
      <div className="mx-auto w-full max-w-7xl 2xl:max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-12">
          {/* Left Column */}
          <div className="lg:col-span-5 space-y-7">
            <div className="space-y-3">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#15803d]">
                ALL YOUR COMPLIANCE NEEDS
              </span>
              <WordReveal
                as="h2"
                text="One platform. Many services."
                className="font-display text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl leading-tight"
                stagger={0.06}
              />
            </div>

            <p className="text-base leading-relaxed text-gray-600 font-normal">
              Choose from a wide range of legal and business services delivered by verified
              Associates.
            </p>

            {/* List with Crisp SVG Green Ticks */}
            <ul className="space-y-3.5 pt-1 text-sm font-medium text-gray-800">
              {[
                'GST Registration & Filing',
                'Company Incorporation',
                'Trademark Registration',
                'Income Tax Services',
                'Business Licenses',
                'And more...',
              ].map((item, idx) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="flex items-center gap-3.5"
                >
                  <span className="flex h-5 fill-[#15803d] w-5 shrink-0 items-center justify-center rounded-full bg-[#15803d] text-white shadow-2xs">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                  <span className="font-semibold text-gray-900">{item}</span>
                </motion.li>
              ))}
            </ul>

            <div className="pt-2">
              <Link to="/client/services">
                <button
                  type="button"
                  className="group inline-flex items-center gap-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 px-6 py-3.5 text-sm font-bold text-gray-900 shadow-2xs hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                >
                  <span>Explore Services</span>
                  <span className="text-base transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </button>
              </Link>
            </div>
          </div>

          {/* Right Column: Grid of Glassmorphic Service Cards */}
          <div className="lg:col-span-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {serviceCards.map((card, idx) => (
              <GlassmorphicServiceCard key={card.title} card={card} index={idx} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GlassmorphicServiceCard({ card, index }: { card: GlassmorphicCardItem; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{
        duration: 0.7,
        delay: index * 0.1,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      <Link
        to="/client/services"
        className="group relative flex flex-col justify-between rounded-2xl border border-gray-200/90 bg-white/95 p-6 shadow-xs backdrop-blur-md hover:border-[#15803d]/70 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden min-h-[175px]"
      >
        {/* Background Ambient Glow Effect on Hover */}
        <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-emerald-500/15 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="space-y-3 relative z-10">
          {/* Top Header: Title on Left + Arrow on Right */}
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-base font-bold text-gray-900 group-hover:text-[#15803d] transition-colors leading-snug">
              {card.title}
            </h3>
            <span className="text-gray-400 group-hover:text-[#15803d] transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </div>

          {/* Description */}
          <p className="text-xs sm:text-sm text-gray-500 leading-relaxed font-normal">
            {card.desc}
          </p>
        </div>

        {/* Tags Pills at Bottom */}
        <div className="mt-5 flex flex-wrap items-center gap-1.5 pt-3.5 border-t border-gray-100 relative z-10">
          {card.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-gray-50 group-hover:bg-emerald-50/80 px-2.5 py-1 text-[11px] font-semibold text-gray-600 group-hover:text-emerald-800 transition-colors"
            >
              {tag}
            </span>
          ))}
        </div>
      </Link>
    </motion.div>
  );
}
